import { i18n } from '@/i18n';
import { ChartMarkers } from '@/models/common/ChartMarkers';
import { SysarbColors } from '@/types';

export class Common {
    public static convertHexToRgba(hex: string, alpha = 1): string {
        if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
            let rgbValues: string[] = hex.substring(1).split('');
            if (rgbValues.length === 3) {
                rgbValues = [rgbValues[0], rgbValues[0], rgbValues[1], rgbValues[1], rgbValues[2], rgbValues[2]];
            }
            const value: any = `0x${rgbValues.join('')}`;
            return `rgba(${[(value >> 16) & 255, (value >> 8) & 255, value & 255].join(',')}, ${alpha})`;
        }
        throw new Error(`Invalid hex value: ${hex}`);
    }

    public static getGenderColors(key: string): string {
        switch (key) {
            case i18n.global.t('enums.gender.value.1'):
                return SysarbColors.MALE;
            case i18n.global.t('enums.gender.value.0'):
                return SysarbColors.FEMALE;
            case i18n.global.t('enums.gender.value.2'):
                return SysarbColors.NOT_DECLARED;
            case '1':
                return SysarbColors.MALE;
            case '0':
                return SysarbColors.FEMALE;
            case '2':
                return SysarbColors.NOT_DECLARED;
            default:
                return '';
        }
    }

    public static getGenderMarker(key: string): string {
        switch (key) {
            case i18n.global.t('enums.gender.value.1'):
                return ChartMarkers[2];
            case i18n.global.t('enums.gender.value.0'):
                return ChartMarkers[0];
            case i18n.global.t('enums.gender.value.2'):
                return ChartMarkers[1];
            default:
                return '';
        }
    }

    public static isSwedishSocialSecurityNumber(ssn: string): boolean {
        // Swedish SSN pattern: (YY)YYMMDD-XXXX or (YY)YYMMDD+XXXX
        const ssnPattern = /^((19|20)?[0-9]{2})?[0-9]{6}[-+][0-9]{4}$/;
        return ssnPattern.test(ssn);
    }

    public static getCenturyFromDate(yearString: string): string {
        //Ensure length
        if (yearString.length < 2 && yearString.length < 4) throw new Error('Invalid date length');

        const currentYear = new Date().getFullYear();
        const minYear = currentYear - 15;

        const currentYearSplit = parseInt(minYear.toString().substring(2, 4));
        const dateStringSplit = parseInt(yearString.length === 2 ? yearString : yearString.substring(2, 4));

        return dateStringSplit >= 0 && dateStringSplit <= currentYearSplit ? '20' : '19';
    }

    /**
     * Generates a swedish ssn in the standard format from a given ssn, if non swedish ssn is provided it will return the same ssn.
     * @param { string } ssn The social security number to standardize.
     * @returns { string } The standardized social security number or the same ssn if it is not a swedish one.
     */
    public static generateSocialSecurityNumber(ssn: string): string {
        if (!Common.isSwedishSocialSecurityNumber(ssn)) return ssn;

        // Valid social security number, this is our base.
        if (ssn.length === 13) return ssn;

        //Append a dash to make it a valid one.
        if (ssn.length === 12) {
            const XXXX = ssn.substring(8, 12);
            const date = ssn.substring(0, 8);

            return `${date}-${XXXX}`;
        }

        ssn = ssn.replace('-', '');

        //Append century to make it a valid one.
        if (ssn.length === 10) {
            const year = ssn.slice(0, 2);
            const month = ssn.slice(2, 4);
            const day = ssn.slice(4, 6);
            const XXXX = ssn.slice(6, 10);
            const century = this.getCenturyFromDate(year);

            const newSSN = `${century}${year}${month}${day}-${XXXX}`;
            return newSSN;
        }

        return '';
    }

    public static createDictionary(list: any[], dictionaryKey: string, itemKey?: string, mapIsArray = true): Map<string, any> {
        const newDictionary: Map<string, any> = new Map<string, any>();
        list?.forEach((item: any) => {
            const itemValue = itemKey !== undefined ? Common.getDynamicPropertyValue(itemKey, item) : item;
            const mapKey = Common.getDynamicPropertyValue(dictionaryKey, item).toString();
            if (mapIsArray) {
                if (newDictionary.has(mapKey)) {
                    newDictionary.get(mapKey).push(itemValue);
                } else {
                    newDictionary.set(mapKey, [itemValue]);
                }
            } else {
                newDictionary.set(mapKey.toString(), itemValue);
            }
        });
        return newDictionary;
    }

    public static getDynamicPropertyValue(key: string, item: unknown): any {
        return key.split('.').reduce((object, segment) => {
            if (object != null) {
                const arrayIndexMatch = RegExp(/^(\w+)\[(\d+)\]$/).exec(segment);
                if (arrayIndexMatch != null) {
                    const propertyName = arrayIndexMatch[1];
                    const index = parseInt(arrayIndexMatch[2], 10);
                    return (object as any)[propertyName]?.[index];
                }

                if (Object.hasOwn(object, segment)) {
                    return (object as any)[segment];
                }
            }
            return undefined;
        }, item);
    }

    public static getYearAndMonthStringFromPercentage(datePercentage: number): string {
        // Round down to get the full year and amount of months
        const years = Math.floor(datePercentage);
        const months = datePercentage * 12 - (years) * 12;
        const roundedMonth = Math.round(months);
        // Add translation for years and months
        const yearsString = i18n.global.t('system.year', years);
        const monthsString = i18n.global.t('system.month', roundedMonth);
        let string = '';
        // Create human readable date string.
        if (years >= 1) {
            string = `${years} ${yearsString}`;
            if (months > 1) {
                string += `, ${roundedMonth} ${monthsString}`;
            }
        } else if (months > 1) {
            string = `${roundedMonth} ${monthsString}`;
        } else {
            string = i18n.global.t('components.common.lessThanAMonth');
        }
        return string;
    }

    public static getYearAndMonthStringFromDate(date: Date | string | null | undefined): string {
        const percentage = Common.getDateDiffInPercentage(new Date(), date);
        // Compares and calculates dates in percentage and then gets result in decimals
        return Common.getYearAndMonthStringFromPercentage(percentage);
    }

    public static colorHue(color: string, percentage: number): string {
        let R = parseInt(color.substring(1, 3), 16);
        let G = parseInt(color.substring(3, 5), 16);
        let B = parseInt(color.substring(5, 7), 16);

        R = Math.round((R * (100 + percentage)) / 100);
        G = Math.round((G * (100 + percentage)) / 100);
        B = Math.round((B * (100 + percentage)) / 100);

        R = R > 255 ? 255 : R < 0 ? 0 : R;
        G = G > 255 ? 255 : G < 0 ? 0 : G;
        B = B > 255 ? 255 : B < 0 ? 0 : B;

        const Rstr = R.toString(16);
        const Gstr = G.toString(16);
        const Bstr = B.toString(16);

        const RR = ((Rstr.length === 1) ? `0${Rstr}` : Rstr);
        const GG = ((Gstr.length === 1) ? `0${Gstr}` : Gstr);
        const BB = ((Bstr.length === 1) ? `0${Bstr}` : Bstr);

        return `#${RR}${GG}${BB}`;
    }

    public static calculateYears(currentYear: number, fromDate: Date | null | undefined): number {
        const fromYear = Common.getYear(fromDate);
        if (fromYear === 0) {
            return 0;
        }
        return currentYear - fromYear;
    }

    public static calculateAge(birthDate: Date, startDate?: Date): number {
        // If no start date is provided, use current date
        const providedDate = startDate ?? new Date();

        // Get birth year and month
        const birthYear = Common.getYear(birthDate);
        const birthMonth = Common.getMonth(birthDate);

        // Get provided year and month
        const providedYear = Common.getYear(providedDate);
        const providedMonth = Common.getMonth(providedDate);

        // Calculate age
        let age = providedYear - birthYear;
        if (providedMonth < birthMonth) {
            age -= 1;
        }
        // if age is a negative number, return 0
        return age < 0 ? 0 : age;
    }

    public static getDateDiffInPercentage(currentDate: Date | string | null | undefined, fromDate: Date | string | null | undefined): number {
        if (currentDate == null || fromDate == null) return 0;
        const yearDiff = Common.getYear(currentDate) - Common.getYear(fromDate);
        const monthDiff = (Common.getMonth(currentDate) - Common.getMonth(fromDate));
        const dayDiff = Common.getDay(currentDate) - Common.getDay(fromDate);
        if (yearDiff === 0 && monthDiff === 0 && dayDiff === 0) return 0;
        return ((yearDiff * 12 + monthDiff) / 12);
    }

    public static getYear(date: Date | string | null | undefined): number {
        if (date == null) {
            return 0;
        }

        // Depending on type, get the year

        if (typeof date === 'string') {
            return new Date(date).getFullYear();
        }

        return date.getFullYear();
    }

    public static getMonth(date: Date | string | null | undefined): number {
        if (date == null) {
            return 0;
        }

        // Depending on type, get the month

        if (typeof date === 'string') {
            return new Date(date).getMonth();
        }

        return date.getMonth();
    }

    public static getDay(date: Date | string | null | undefined): number {
        if (date == null) {
            return 0;
        }

        // Depending on type, get the day

        if (typeof date === 'string') {
            return new Date(date).getDay();
        }

        return date.getDay();
    }

    public static addLeadingZero(value: number): string {
        return `${value < 10 ? '0' : ''}${value}`;
    }

    /**
     * Converts the selected string value to lower camel case.
     * @param value The string value to convert to lower camel case.
     */
    public static toLowerCamelCase(value: string): string {
        // Special case for ID fields
        if (value === 'ID') { return 'id'; }

        const newValue = value.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => (index === 0 ? word.toLowerCase() : word)).replace(/\s+/g, '');
        return newValue;
    }

    /**
     * Converts the selected string value to upper pascal case.
     * @param value The string value to convert to upper pascal case.
     */
    public static toUpperPascalCase(value: string): string {
        // Special case for ID fields
        if (value === 'id') { return 'ID'; }

        const newValue = value.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => (index === 0 ? word.toUpperCase() : word)).replace(/\s+/g, '');
        return newValue;
    }

    /**
     * Takes a number array and returns the median value.
     * @param values The number array to reduce to a median value.
     */
    public static medianValue(values: number[], roundValue = true): number {
        if (values?.length === 0) {
            return 0;
        }

        // sort the values
        values.sort((a, b) => a - b);
        const { length } = values;

        // if the length is even, take the average of the middle two numbers, otherwise take the middle number
        const medianValue = length % 2 === 0 ? (values[length / 2 - 1] + values[length / 2]) / 2 : values[(length - 1) / 2];
        return roundValue
            ? Math.round(medianValue)
            : medianValue;
    }

    /**
     * Takes a number array and returns the mean value.
     * @param values The number array to reduce to an mean value.
     */
    public static meanValue(values: number[], roundValue = true): number {
        if (values?.length === 0) {
            return 0;
        }

        const meanValue = values.reduce((a, b) => a + b) / values.length;
        return roundValue
            ? Math.round(meanValue)
            : meanValue;
    }

    /**
     * Takes a number array and returns the min value.
     * @param values The number array to reduce to a min value.
     */
    public static minValue(values: number[]): number {
        if (values?.length === 0) {
            return 0;
        }
        return Math.min(...values);
    }

    /**
     * Takes a number array and returns the max value.
     * @param values The number array to reduce to a max value.
     */
    public static maxValue(values: number[]): number {
        if (values?.length === 0) {
            return 0;
        }
        return Math.max(...values);
    }

    /**
     * Takes a number array and desired percentile rank and returns the perctile value.
     * @param values The number array.
     * @param percentile The required recentile rank .
     */
    public static getPercentile(values: number[], percentile: number): number {
        if (values.length === 0) return 0;
        values.sort((a, b) => a - b);

        let percentage = percentile * values.length;
        percentage = Math.floor(percentage);
        const result = values[percentage];
        return result;
    }

    public static splitOnThousands(value: number, character: string): string {
        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, character);
    }

    /**
     * Pad a number to make it even. It can be used when setting min/max values that should be even thousands.
     * @param value The number to be padded.
     * @param roundUp True if rounded upwards, otherwise down.
     */
    public static getPaddedValue(value: number, roundUp: boolean): number {
        const padding = 3000;
        const roundBy = 1000;
        let roundedValue = roundUp
            ? Math.ceil(value / roundBy)
            : Math.floor(value / roundBy);
        roundedValue *= roundBy;
        return roundUp
            ? Common.minZero(roundedValue + padding)
            : Common.minZero(roundedValue - padding);
    }

    /**
     * Return a positive number.
     * @param value The number to check
     */
    public static minZero(value: number): number {
        return value < 0 ? 0 : value;
    }

    /**
     * Checks if the email address is in a correct format.
     * @param email The email string to test
     */
    public static isValidEmail(email: string): boolean {
        return /.+@.+\..+/.test(email);
    }

    /**
     * Format numbers to never exceed 2 decimals and be divided on thousands
     * @param value Value to format
     */
    public static formatNumber(value: number, decimalCount = 2): string {
        // Limit all decimal numbers to 2 decimal points. If the value is not a decimal don't show the extra decimals
        const decimals = Number.isInteger(value) ? 0 : decimalCount;
        return Common.splitOnThousands(Number(value.toFixed(decimals)), ' ');
    }

    public static calculateDifference(currentValue: number, newValue: number): number {
        let value = Math.abs(((currentValue / newValue) * 100) - 100);
        value = Number.isNaN(value) ? 0 : value;
        return value;
    }

    public static validateAndFormatNumber(value: string | number, decimalCount?: number): string {
        // Do a check if the number is a type of string and convert it into a number.
        if (typeof value === 'string' && value !== '') {
            const convertedNumberString = Number(value);
            if (!Number.isNaN(convertedNumberString)) {
                return Common.formatNumber(convertedNumberString, decimalCount);
            }
        }

        if (typeof value === 'number') {
            return Common.formatNumber(value);
        }

        return value;
    }
}
